import React from 'react';
import { Button, LoadingOverlay, PasswordInput, SimpleGrid, Text, Box, Flex } from '@mantine/core';
import { ILinkedEmail } from '@/entities/admin-app/email';
import { useLazyChangePasswordLinkedUserEmailQuery } from '@/entities/admin-app/email/api';
import { showNotification } from '@mantine/notifications';
import { useStyles } from '@/containers/pages/user/components/sections/linked-emails/styles';
import { z } from 'zod';
import { FormErrors, useForm } from '@mantine/form';
import { getValidateErrors } from '@/lib/utils/validation';
import { useTranslation } from 'react-i18next';
import { extractErrorCode } from '@/lib/utils/extractFetchErrors';
import { defaultMailServerPasswordOptions } from '@/mock-data/passwordOptions';
import { usePasswordSchema } from '@hooks/password/useGeneratePolicy';
import { ErrorsEnum } from '@/types/enums';
import { IUsePopUpUI } from '@components/UI/PopUp';
import { useMediaQuery } from '@mantine/hooks';

interface IChangePasswordModalProps extends IUsePopUpUI {
  selectedEmail: ILinkedEmail | null;
  onSuccess?: () => void;
}
export const ChangePasswordModal = (props: IChangePasswordModalProps) => {
  const { selectedEmail, setIsOpen, onSuccess } = props;
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [changeEmailPassword, { isFetching, isError }] =
    useLazyChangePasswordLinkedUserEmailQuery();

  const modalClose = () => {
    passwordsForm.reset();
    setIsOpen(false);
  };

  const passwordRulesZ = usePasswordSchema(defaultMailServerPasswordOptions, t);

  const passwordsFormSchema = z.object({
    Password: z.string().min(1, { message: t('profile.addOldPassword') }),
    NewPassword: passwordRulesZ
  });

  const passwordsForm = useForm({
    initialValues: {
      Password: '',
      NewPassword: ''
    },
    validateInputOnChange: true,
    validate: (values: any): FormErrors => getValidateErrors(passwordsFormSchema, values)
  });

  const handleChangePasswords = async () => {
    const { Password, NewPassword } = passwordsForm.values;
    try {
      const res = await changeEmailPassword({
        EmailId: selectedEmail?.Id || 0,
        Password,
        NewPassword
      });

      if ('error' in res) {
        const codeError = extractErrorCode(res.error);
        const nativeErrorCode = (res as any)?.error?.data.ErrorCode;
        showNotification({
          title:
            nativeErrorCode === 406 || codeError === ErrorsEnum.InvalidLoginOrPassword
              ? t('incorrectOldEmailPassword')
              : '',
          message: '',
          autoClose: true,
          color: 'red',
          style: { zIndex: 1003 }
        });
      } else {
        showNotification({
          title: t('profile.updatePasswordSuccessMessage'),
          message: '',
          autoClose: true,
          color: 'green',
          style: { zIndex: 1003 }
        });

        if (onSuccess) {
          onSuccess();
        }

        modalClose();
      }
    } catch (err: any) {
      console.warn('Ошибка смены пароля', err);
    }
  };

  const isMobile = useMediaQuery('(max-width: 768px)');

  return (
    <Flex w={isMobile ? '100%' : 600} p={12} direction="column" gap={12}>
      <Box className={classes.modalContainer}>
        <Text>{t('profile.passwordChangeWarning')}</Text>
        <SimpleGrid cols={1} mt={16} w="100%">
          <PasswordInput
            placeholder={t('profile.password')}
            label={t('profile.oldPassword')}
            withAsterisk
            {...passwordsForm.getInputProps('Password')}
          />
          <PasswordInput
            placeholder={t('profile.newPassword')}
            label={t('profile.newPassword')}
            withAsterisk
            {...passwordsForm.getInputProps('NewPassword')}
          />
        </SimpleGrid>
      </Box>
      <Flex justify="space-between" gap={12}>
        <Button
          fullWidth
          disabled={!passwordsForm.isValid() || !selectedEmail}
          onClick={handleChangePasswords}
          variant="filled"
        >
          {t('profile.passwordChange')}
        </Button>
        <Button fullWidth color="gray" variant="outline" onClick={modalClose}>
          {t('profile.cancel')}
        </Button>
      </Flex>
      <LoadingOverlay visible={isFetching && !isError} overlayProps={{ blur: 2 }} />
    </Flex>
  );
};
